<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Avatar from 'next/avatar/Avatar.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import ChannelName from 'dashboard/routes/dashboard/settings/inbox/components/ChannelName.vue';
import { picoSearch } from '@scmmishra/pico-search';
import Input from 'next/input/Input.vue';

const { t } = useI18n();
const router = useRouter();

const searchQuery = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');

const whatsappInboxes = computed(() => {
  return (inboxes.value || [])
    .filter(i => i.channel_type === 'Channel::Whatsapp' && !i.disabled)
    .sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return whatsappInboxes.value;
  return picoSearch(whatsappInboxes.value, query, ['name', 'phone_number']);
});

const selectInbox = inboxId => {
  router.push({
    name: 'orders_payments_list',
    params: { inboxId },
  });
};

onMounted(async () => {
  // Automatically redirect if there's only 1 inbox
  if (whatsappInboxes.value.length === 1) {
    selectInbox(whatsappInboxes.value[0].id);
  }
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <span class="text-heading-1 text-n-slate-12">
            {{ t('ORDERS_PAGE.TITLE') }}
          </span>
        </div>
        <!-- Filter bar -->
        <div class="flex items-center gap-3 pb-4">
          <div class="flex-1 max-w-xs">
            <Input
              id="searchQuery"
              v-model="searchQuery"
              name="search_query"
              :placeholder="t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
              custom-input-class="ltr:!pl-8 rtl:!pr-8"
              size="sm"
              type="search"
            >
              <template #prefix>
                <span
                  class="i-lucide-search size-4 absolute top-1/2 -translate-y-1/2 text-n-slate-11 ltr:left-2.5 rtl:right-2.5"
                />
              </template>
            </Input>
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-4">
        <span
          v-if="!filteredInboxesList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ t('INBOX_MGMT.NO_RESULTS') }}
        </span>
        <div
          v-else-if="!whatsappInboxes.length"
          class="flex flex-col items-center justify-center py-20 text-center"
        >
          <span class="text-n-slate-11">
            {{ t('INBOX_MGMT.LIST.404') }}
          </span>
        </div>
        <div v-else class="divide-y divide-n-weak border-t border-n-weak">
          <div
            v-for="inbox in filteredInboxesList"
            :key="inbox.id"
            class="flex justify-between flex-row items-center gap-4 py-4 hover:bg-n-alpha-1 cursor-pointer px-4 -mx-4 rounded-xl transition-colors"
            @click="selectInbox(inbox.id)"
          >
            <div class="flex items-center gap-4">
              <div
                v-if="inbox.avatar_url"
                class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
              >
                <Avatar
                  :src="inbox.avatar_url"
                  :name="inbox.name"
                  :size="24"
                  rounded-full
                />
              </div>
              <div
                v-else
                class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
              >
                <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
              </div>
              <div class="flex flex-col items-start gap-1">
                <span class="block text-heading-3 text-n-slate-12 capitalize">
                  {{ inbox.name }}
                </span>
                <ChannelName
                  :channel-type="inbox.channel_type"
                  :medium="inbox.medium"
                  class="text-body-main text-n-slate-11"
                />
              </div>
            </div>
            <div class="flex gap-3 justify-end">
              <span class="i-lucide-chevron-right text-n-slate-8" />
            </div>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>
